import DeleteIcon from "@mui/icons-material/DeleteOutline";
import CopyIcon from "@mui/icons-material/FileCopyOutlined";
import LinkIcon from "@mui/icons-material/Link";
import SendIcon from "@mui/icons-material/Send";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { ConfirmDialogType, ConfirmResponse } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import Loader from "_shared/_views/loading/loader";
import LoadingButton from "_shared/_views/loading/loading-button";
import EmailAddress from "_shared/email-addresses/email-address";
import EmailAddressSchema from "_shared/email-addresses/email-address-schema";
import Guid from "_shared/guids/guid";
import Name from "_shared/names/name";
import axios from "axios";
import * as Config from "config";
import RegistrationInfo from "legal-entities/registration-info/registration-info";
import UserEntityMemberInvitationAPIService from "legal-entities/invitations/member-invitations/email-based/_api/user-entity-member-invitation-api-service";
import UserEntityMemberInvitation from "legal-entities/invitations/member-invitations/email-based/user-entity-member-invitation";
import OpenEntityMemberInvitationAPIService, {
  OpenEntityMemberInvitationAPIServiceError,
} from "legal-entities/invitations/member-invitations/open/_api/open-entity-member-invitation-api-service";
import OpenEntityMemberInvitation from "legal-entities/invitations/member-invitations/open/open-entity-member-invitation";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useState } from "react";
import { useSession } from "users/__session/session-context";
import UserEntityInfo from "users/information/entity-information/user-entity-info";
import EntityName from "legal-entities/entity-names/entity-name";

const InvitationFormDescription = Typography;
const DirectMemberInvitationSubForm = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  "& .MuiTextField-root": {
    width: "100%",
  },
}));
const FieldGroup = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  marginTop: theme.spacing(2),
  width: "100%",
  "& .MuiTextField-root": {
    width: "100%",
  },
}));
const SendInvitationButton = styled(LoadingButton)(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

const FormDivider = styled(Divider)(({ theme }) => ({
  margin: theme.spacing(4, 0),
}));

const OpenInvitationLinks = styled(Table)(() => ({
  "& td": {
    "&:first-of-type": {
      paddingLeft: 0,
    },
    "&:last-of-type": {
      paddingRight: 0,
    },
    borderBottom: "none",
  },
}));
const CreateInvitationLink = styled(LoadingButton)(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

type EntityMemberInvitationFormProps = {
  className?: string;
  confirmDialog: ConfirmDialogType;
};
export default function EntityMemberInvitationForm(props: Readonly<EntityMemberInvitationFormProps>) {
  const { className, confirmDialog } = props;

  const session = useSession();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSending, setIsSending] = useState<boolean>(false);

  const [focusedOpenInvitation, setFocusedOpenInvitation] = useState<OpenEntityMemberInvitation | null>(null);
  const [focusedOpenInvitationURL, setFocusedOpenInvitationURL] = useState<URL | null>(null);

  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [emailAddressValue, setEmailAddressValue] = useState<string>("");

  const [creating, setCreating] = useState<boolean>(false);

  const loadInvitations = useCallback(async () => {
    if(isLoading) return;
    setIsLoading(true);
    try {
      const service = new OpenEntityMemberInvitationAPIService(session);

      const openInvitations = await service.getAllOpenEntityMemberInvitationAsync(session.user!.legalEntityId);
      if (openInvitations.length === 0) return;

      setFocusedOpenInvitation(openInvitations[0]);
      setFocusedOpenInvitationURL(buildInvitationURL(openInvitations[0].id));
    } catch (ex) {
      console.error(ex);
      enqueueSnackbar("Failed to get invitations", { variant: "error" });
    } finally {
      setIsLoading(false);
    }
  }, [session, isLoading]);

  useEffect(() => {
    void loadInvitations();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createInvitationLink = async (entityId: Guid) => {
    setCreating(true);
    try {
      const service = new OpenEntityMemberInvitationAPIService(session);
      const createdInvitation = await service.createOpenMemberInvitation(entityId);

      setFocusedOpenInvitation(createdInvitation);
      setFocusedOpenInvitationURL(buildInvitationURL(createdInvitation.id));

      await loadInvitations();

      enqueueSnackbar("Created new invitation link", { variant: "success" });
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Couldn't get invitation link. Please try again.", { variant: "error" });
    } finally {
      setCreating(false);
    }
  };
  const buildInvitationURL = (invitationId?: Guid): URL | null => {
    if (!invitationId) return null;
    return new URL(`${Config.entityMemberInvitationLink}/${invitationId.value}`);
  };

  const sendInvitation = async () => {
    try {
      setIsSending(true);
      const legalEntityId = session.user!.legalEntityId;
      const companyName = session.user!.companyName;
      const name = Name.create({ params: { firstName, lastName } });
      const email = EmailAddress.create(emailAddressValue);
      const registrationInfo = RegistrationInfo.create({ params: { name, email }, options: { silenceSchemaErrors: true } });

      const payload = UserEntityMemberInvitation.create({
        userEntityInfo: new UserEntityInfo({
          entityId: legalEntityId,
          entityName: new EntityName(companyName!),
          isAdmin: false,
          isManager: false,
          isOfficer: false,
          entityVendorFlag: session.user?.isVendor,
          entityClientFlag: session.user?.isClient,
        }),
        invitedEmail: email,
        registrationInfo,
      });
      const service = new UserEntityMemberInvitationAPIService(session);
      await service.createUserEntityMemberInvitation(payload);

      setFirstName("");
      setLastName("");
      setEmailAddressValue("");

      enqueueSnackbar("Invitation sent", { variant: "success" });
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to send invitation", { variant: "error" });
    } finally {
      setIsSending(false);
    }
  };

  const deleteInvitationLink = async (invitation: OpenEntityMemberInvitation) => {
    try {
      const confirmResponse = await confirmDialog({
        title: "Delete Invitation",
        message: "Are you sure you want to delete this invitation?",
        okButtonText: "Delete",
        cancelButtonText: "Cancel",
      });

      if (confirmResponse === ConfirmResponse.Cancel) return;

      const service = new OpenEntityMemberInvitationAPIService(session);
      await service.deleteOpenMemberInvitation(invitation);

      setFocusedOpenInvitation(null);

      enqueueSnackbar("Deleted invitation link", { variant: "success" });
    } catch (error) {
      console.error(error);
      if (error instanceof OpenEntityMemberInvitationAPIServiceError && axios.isAxiosError(error.error)) {
        enqueueSnackbar(error.error.response?.data, { variant: "error" });
        return;
      }
      enqueueSnackbar("Failed to delete invitation", { variant: "error" });
    }
  };

  const copyToClipboard = async (textToCopy?: string) => {
    if (!textToCopy) {
      enqueueSnackbar("No link to copy", { variant: "warning" });
      return;
    }
    await navigator.clipboard.writeText(textToCopy);
    enqueueSnackbar("Copied link to clipboard", { variant: "info" });
  };

  return (
    <div className={className}>
      <InvitationFormDescription>
        Invite a new member to your entity by entering their name and email address below:
      </InvitationFormDescription>
      <DirectMemberInvitationSubForm>
        <FieldGroup gap={1}>
          <TextField
            label="First Name"
            value={firstName}
            helperText="required"
            required={true}
            margin="normal"
            variant="outlined"
            onChange={(e) => setFirstName(e.target.value)}
          />
          <TextField
            label="Last Name"
            value={lastName}
            helperText="required"
            required={true}
            margin="normal"
            variant="outlined"
            onChange={(e) => setLastName(e.target.value)}
          />
        </FieldGroup>
        <TextField
          label="Email Address"
          value={emailAddressValue}
          helperText={
            emailAddressValue.length > 0 && !EmailAddressSchema.isValid(emailAddressValue)
              ? "Invalid email address"
              : "required"
          }
          required={true}
          error={emailAddressValue.length > 0 && !EmailAddressSchema.isValid(emailAddressValue)}
          margin="dense"
          variant="outlined"
          onChange={(e) => setEmailAddressValue(e.target.value)}
        />
      </DirectMemberInvitationSubForm>
      <SendInvitationButton
        endIcon={<SendIcon />}
        loading={isSending}
        color={"primary"}
        variant={"contained"}
        disabled={isSending || !EmailAddressSchema.isValid(emailAddressValue)}
        onClick={sendInvitation}
      >
        Send
      </SendInvitationButton>

      <FormDivider />

      <InvitationFormDescription>
        Or create a link that you can give to anyone you want to invite:
      </InvitationFormDescription>
      {focusedOpenInvitation !== null && (
        <OpenInvitationLinks>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell>
                  <Loader />
                </TableCell>
              </TableRow>
            )}
            <TableRow key={focusedOpenInvitation.id?.value}>
              <TableCell align="left">
                <Typography variant={"subtitle2"}>{focusedOpenInvitationURL?.toString()}</Typography>
              </TableCell>
              <TableCell align="left">
                <Typography variant={"body2"}>
                  Expires {focusedOpenInvitation.expires?.format("M/D/YY h:mm A")}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Button
                  endIcon={<CopyIcon />}
                  color="primary"
                  onClick={() => copyToClipboard(focusedOpenInvitationURL?.toString())}
                >
                  Copy
                </Button>
                <Button
                  endIcon={<DeleteIcon />}
                  color="error"
                  onClick={() => deleteInvitationLink(focusedOpenInvitation)}
                >
                  Delete
                </Button>
              </TableCell>
            </TableRow>
          </TableBody>
        </OpenInvitationLinks>
      )}
      <CreateInvitationLink
        startIcon={<LinkIcon />}
        variant="contained"
        color="primary"
        loading={creating}
        disabled={focusedOpenInvitation !== null}
        onClick={() => createInvitationLink(session.user!.legalEntityId)}
      >
        Create Invite Link
      </CreateInvitationLink>
    </div>
  );
}
